import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { getInsightBySlug, insights } from "@/data/insights";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { useReveal } from "@/hooks/useReveal";
import { buildWhatsAppLink } from "@/lib/whatsapp";

export default function Insight() {
  const { slug } = useParams();
  const article = getInsightBySlug(slug);
  const rootRef = useReveal();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!article) return <Navigate to="/" replace />;

  const currentIdx = insights.findIndex((i) => i.slug === slug);
  const next = insights[(currentIdx + 1) % insights.length];

  return (
    <div ref={rootRef} data-testid={`insight-page-${slug}`} className="bg-white">
      <Navbar />

      <main className="pt-32 md:pt-44">
        {/* Header */}
        <section className="max-w-3xl mx-auto px-6 md:px-10">
          <Link
            to="/#insights"
            data-testid="insight-back-link"
            className="inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase font-semibold text-[#6b6b6b] hover:text-[#0a0a0a] transition-colors"
          >
            <span aria-hidden>←</span> Voltar para Insights
          </Link>

          <div className="mt-10 flex items-center gap-4">
            <span className="rule-yellow" />
            <span className="eyebrow">{article.category}</span>
          </div>

          <h1
            data-testid="insight-title"
            className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.06] tracking-tight text-[#0a0a0a] mt-8"
          >
            {article.title}
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#3a3a3a] leading-relaxed max-w-2xl">
            {article.subtitle}
          </p>
          <div className="mt-10 flex items-center gap-6 text-[12px] tracking-[0.18em] uppercase text-[#6b6b6b]">
            <span>{article.readingTime}</span>
            <span className="w-1 h-1 bg-[#6b6b6b] rounded-full" />
            <span>Insight {article.number}</span>
          </div>
        </section>

        {/* Yellow editorial separator */}
        <div className="max-w-3xl mx-auto px-6 md:px-10 mt-16">
          <div className="h-px bg-[#0a0a0a]" />
        </div>

        {/* Article body */}
        <article className="max-w-3xl mx-auto px-6 md:px-10 mt-16 article-prose reveal">
          {article.paragraphs.slice(0, 2).map((p, i) => (
            <p key={`p-top-${i}`}>{p}</p>
          ))}

          <blockquote>{article.quote}</blockquote>

          {article.paragraphs.slice(2).map((p, i) => (
            <p key={`p-bot-${i}`}>{p}</p>
          ))}
        </article>

        {/* Closing CTA */}
        <section className="bg-[#fafaf7] mt-28 md:mt-36 py-20 md:py-28">
          <div className="max-w-3xl mx-auto px-6 md:px-10 reveal">
            <p className="eyebrow text-[#7a5a00]">Oasys Academy</p>
            <h2 className="font-display text-3xl md:text-5xl leading-[1.08] tracking-tight text-[#0a0a0a] mt-6">
              Aprenda a transformar leitura em direção.
            </h2>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-[#3a3a3a] max-w-2xl">
              A Oasys Academy forma o profissional que pensa o salão como
              um negócio: técnica, dados, relacionamento e crescimento
              organizado.
            </p>
            <a
              href={buildWhatsAppLink(
                `Olá! Li o insight "${article.title}" e quero conhecer a Oasys Academy.`,
              )}
              target="_blank"
              rel="noreferrer"
              data-testid="insight-cta-whatsapp"
              className="btn-primary mt-10"
            >
              Falar com a Academy
              <span aria-hidden>→</span>
            </a>
          </div>
        </section>

        {/* Next article */}
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="eyebrow text-[#6b6b6b]">Próximo insight</p>
          <Link
            to={`/insights/${next.slug}`}
            data-testid="insight-next-link"
            className="group flex flex-col md:flex-row md:items-end md:justify-between gap-6 mt-6 pt-8 border-t border-[#0a0a0a]"
          >
            <h3 className="font-display text-3xl md:text-5xl leading-[1.05] tracking-tight text-[#0a0a0a] group-hover:text-[#7a5a00] transition-colors max-w-3xl">
              {next.title}
            </h3>
            <span className="text-[12px] tracking-[0.18em] uppercase font-semibold text-[#0a0a0a] inline-flex items-center gap-2">
              Ler agora
              <span
                aria-hidden
                className="inline-block transition-transform group-hover:translate-x-1"
              >
                →
              </span>
            </span>
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
}
